import { NavLink } from 'react-router-dom'
import { 
  LayoutDashboard, 
  CreditCard, 
  Wallet, 
  Users, 
  Settings,
  LogOut,
  Menu,
  X,
  Receipt,
  BookOpen,
  ArrowRightLeft,
  AlertTriangle
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useBranding } from '../../contexts/BrandingContext'
import { useState } from 'react'
import Modal from '../ui/Modal'
import Button from '../ui/Button'

const menuItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/transactions', label: 'Transactions', icon: CreditCard },
  { path: '/payouts', label: 'Payouts', icon: Wallet },
  { path: '/fund-transfer', label: 'Fund Transfer', icon: ArrowRightLeft },
  { path: '/beneficiaries', label: 'Beneficiaries', icon: Users },
  { path: '/settlements', label: 'Settlements', icon: Receipt },
  { path: '/passbook', label: 'Passbook', icon: BookOpen },
  { path: '/settings', label: 'Settings', icon: Settings },
]

const Sidebar = ({ isMobileOpen, onMobileClose, isDesktopOpen = true }) => {
  const { logout } = useAuth()
  const branding = useBranding()
  const primary = branding.primaryColor || 'var(--primary-color)'
  const siteName = branding.siteName || 'BlackTape'
  const [showLogoutModal, setShowLogoutModal] = useState(false)

  const handleLogout = () => {
    setShowLogoutModal(true)
  }

  const confirmLogout = () => {
    logout()
    window.location.href = '/login'
  }

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onMobileClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out ${
          // Mobile behavior
          isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        } ${
          // Desktop behavior - always fixed, just translate
          isDesktopOpen ? 'lg:translate-x-0' : 'lg:-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h1 className="text-xl font-bold truncate inline-flex items-center gap-2" style={{ color: primary }}>
              {branding.logoUrl ? (
                <img src={branding.logoUrl} alt="" className="h-7 w-7 object-contain" />
              ) : null}
              {siteName}
            </h1>
            <button
              onClick={onMobileClose}
              className="lg:hidden"
              style={{ color: primary }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.8' }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '1' }}
            >
              <X size={24} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {menuItems.map((item) => {
              const Icon = item.icon
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={onMobileClose}
                  className={({ isActive }) =>
                    `flex items-center px-4 py-3 rounded-lg transition-colors ${
                      isActive
                        ? 'text-white font-medium'
                        : ''
                    }`
                  }
                  style={({ isActive }) => {
                    if (isActive) {
                      return { backgroundColor: primary, color: '#ffffff' }
                    }
                    return { color: primary, backgroundColor: 'transparent' }
                  }}
                  onMouseEnter={(e) => {
                    const isActive = e.currentTarget.classList.contains('text-white')
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = 'var(--primary-50)'
                      e.currentTarget.style.color = primary
                    }
                  }}
                  onMouseLeave={(e) => {
                    const isActive = e.currentTarget.classList.contains('text-white')
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = 'transparent'
                      e.currentTarget.style.color = primary
                    }
                  }}
                >
                  <Icon size={20} className="mr-3" />
                  {item.label}
                </NavLink>
              )
            })}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-gray-200">
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-4 py-3 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
            >
              <LogOut size={20} className="mr-3" />
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Logout Confirmation Modal */}
      <Modal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        title={null}
        size="sm"
      >
        <div className="relative">
          {/* Decorative Background Gradient */}
          <div className="absolute -top-6 -left-6 -right-6 h-32 bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 rounded-t-lg opacity-50"></div>
          
          <div className="relative space-y-6">
            {/* Icon Section */}
            <div className="flex justify-center">
              <div className="relative">
                {/* Outer Glow Ring */}
                <div className="absolute inset-0 bg-red-200 rounded-full blur-xl opacity-50 animate-pulse"></div>
                {/* Icon Container */}
                <div className="relative p-5 bg-gradient-to-br from-red-500 to-red-600 rounded-full shadow-lg transform hover:scale-105 transition-transform duration-300">
                  <AlertTriangle className="text-white" size={32} strokeWidth={2.5} />
                </div>
              </div>
            </div>

            {/* Content Section */}
            <div className="text-center space-y-3">
              <h3 className="text-2xl font-bold text-gray-900">
                Confirm Logout
              </h3>
              <p className="text-gray-700 font-medium text-lg">
                Are you sure you want to logout?
              </p>
              <p className="text-sm text-gray-500 leading-relaxed max-w-sm mx-auto">
                You will need to login again to access your dashboard and all your account features.
              </p>
            </div>

            {/* Info Box */}
            <div 
              className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4"
              style={{
                borderRadius: 'var(--border-radius)',
                borderWidth: 'var(--border-width)',
                borderColor: 'var(--border-color)',
                borderStyle: 'solid',
              }}
            >
              <div className="flex items-start gap-3">
                <div className="p-1.5 bg-blue-100 rounded-lg flex-shrink-0 mt-0.5">
                  <LogOut className="text-blue-600" size={16} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-900 mb-1">
                    Session Information
                  </p>
                  <p className="text-xs text-blue-700">
                    Your current session will be terminated and you'll be redirected to the login page.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowLogoutModal(false)}
                className="flex-1 border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-50 font-semibold py-3 transition-all duration-200"
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={confirmLogout}
                className="flex-1 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 font-semibold py-3"
              >
                <LogOut className="mr-2" size={18} />
                Yes, Logout
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default Sidebar

